// Pure, non-React display helpers for turning an accepted planner value into
// human-readable text -- shared by TripPlannerWizard.tsx (the results-screen
// summary), AdjustPreferencesSheet.tsx (collapsed-row labels), and
// TripPlannerQuestions.tsx's own pickers. Split out from
// TripPlannerQuestions.tsx specifically because mixing these plain exports
// into a file that also exports React components defeats Vite Fast Refresh
// for that file (react-refresh/only-export-components) -- this file exports
// no components, only data/functions, so it isn't subject to that rule.
//
// Never touches planner-vocabulary.ts, the RPC contract, or scoring -- every
// value here is either copied straight from an accepted value (capitalize)
// or a display-only relabelling of one (monthFullName, optionLabel).
import type { SurfGoal, TripVibe, WaterTemp, CrowdTolerance } from '@/lib/planner-vocabulary';

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MONTH_FULL_NAMES: Record<string, string> = {
  Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June',
  Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};

/** Expands one of MONTHS' abbreviations to its full name, for display
 * contexts (the results-screen summary, Adjust Preferences) where the
 * 3-letter form used by the picker itself and the RPC is too terse. Falls
 * back to the input unchanged for any value outside MONTHS -- display-only,
 * never touches what's actually sent to the RPC. */
export const monthFullName = (abbr: string): string => MONTH_FULL_NAMES[abbr] ?? abbr;

/** Capitalizes a lowercase enum-shaped value (e.g. skillLevel's 'beginner')
 * for display -- the plain-text-summary equivalent of the `capitalize`
 * Tailwind class TripPlannerQuestions.tsx's pickers already apply visually
 * to the same values. */
export const capitalize = (s: string): string => (s.length ? s[0].toUpperCase() + s.slice(1) : s);

/** Looks up a question's display label for a given accepted value, from the
 * same options array the picker itself renders -- the one place a value's
 * human-readable label is defined, reused for compact display contexts
 * (row summaries, the results-screen preference summary) so a label can
 * never drift from what the picker actually shows for that value. */
export const optionLabel = <T extends string>(
  options: ReadonlyArray<{ value: T; label: string }>,
  value: T,
): string => options.find((o) => o.value === value)?.label ?? value;

// values below must stay in sync with SURF_GOAL_VALUES in planner-vocabulary.ts.
// This stays the full 4-value set (including 'consistent') because it also
// backs optionLabel() lookups for historical values -- a saved handoff or
// Adjust Preferences state from before 'consistent' was retired from the
// picker (see SURF_GOAL_CHOICES below) must still resolve to a real label
// instead of falling back to the raw value string.
export const SURF_GOAL_OPTIONS: ReadonlyArray<{ value: SurfGoal; label: string }> = [
  { value: 'learn', label: 'Learn to Surf' },
  { value: 'progress', label: 'Progress My Surfing' },
  { value: 'score', label: 'Score Great Waves' },
  { value: 'consistent', label: 'Surf More Often' },
];

// The choices actually offered to someone picking a Surf Goal today --
// 'consistent' ("Surf More Often") is intentionally excluded: it wasn't a
// distinct enough goal from the other three to justify a fourth option, but
// the internal value, its SQL scoring branch, and its recommendation-reason
// chip all stay intact for anyone whose saved preferences already carry it.
// Derived from SURF_GOAL_OPTIONS rather than hand-duplicated so the two
// can't drift apart.
export const SURF_GOAL_CHOICES = SURF_GOAL_OPTIONS.filter((o) => o.value !== 'consistent');

// values below must stay in sync with TRIP_VIBE_VALUES in planner-vocabulary.ts
export const TRIP_VIBE_OPTIONS: ReadonlyArray<{ value: TripVibe; label: string }> = [
  { value: 'remote', label: 'Remote & Adventurous' },
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'social', label: 'Social' },
  { value: 'party', label: 'Party' },
];

// values below must stay in sync with CROWD_TOLERANCE_VALUES in planner-vocabulary.ts
export const CROWD_TOLERANCE_OPTIONS: ReadonlyArray<{ value: CrowdTolerance; label: string }> = [
  { value: 'none', label: 'Not at all' },
  { value: 'some', label: 'Somewhat' },
  { value: 'lot', label: 'A lot' },
];

// values below must stay in sync with WATER_TEMP_VALUES in planner-vocabulary.ts
export const WATER_TEMP_OPTIONS: ReadonlyArray<{ value: WaterTemp; label: string }> = [
  { value: 'warm', label: 'Warm' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'cold', label: 'Cold' },
  { value: 'any', label: 'Any' },
];
